import {
  Box,
  Button,
  Checkbox,
  ComboboxItem,
  Grid,
  Group,
  LoadingOverlay,
  Select,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconCheck, IconWindow } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { repositoryMdm } from "../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { MessageResponse } from "../../../model/MessageResponse";
import {
  getCommuneSelectNew,
  getProvinceSelectNew,
} from "../../../service/getSelectApi";

// ATTRIBUTE7 của nhóm: LCN thì màn lập phiếu tự điền người hẹn gặp, SĐT, địa chỉ từ nhóm
export const customerGroupTypes = [
  { value: "KBH", label: "KBH - Khách hàng (nhập tay thông tin hẹn gặp)" },
  { value: "LCN", label: "LCN - Chi nhánh (tự điền thông tin hẹn gặp)" },
];

const WarrantyCustomerGroupForm = ({
  data,
  onSaved,
}: WarrantyCustomerGroupFormProps) => {
  const isEdit = !!data;
  const [visible, { open, close }] = useDisclosure(false);
  const [dataProvince, setDataProvince] = useState<ComboboxItem[]>([]);
  const [dataCommune, setDataCommune] = useState<ComboboxItem[]>([]);

  const form = useForm({
    mode: "controlled",
    initialValues: {
      code: data?.code ?? "",
      name: data?.name ?? "",
      active: data?.active ?? true,
      attribute7: data?.attribute7 ?? "KBH",
      attribute6: data?.attribute6 ?? "",
      attribute1: data?.attribute1 ?? null,
      attribute3: data?.attribute3 ?? null,
      attribute5: data?.attribute5 ?? "",
    },
    validate: {
      code: (value: string) =>
        !value?.trim()
          ? "Vui lòng nhập mã nhóm !"
          : /\s/.test(value.trim())
            ? "Mã nhóm không được có dấu cách !"
            : null,
      name: (value: string) =>
        !value?.trim() ? "Vui lòng nhập tên nhóm !" : null,
      attribute7: (value: string) =>
        !value ? "Vui lòng chọn loại nhóm !" : null,
    },
  });

  const isBranch = form.values.attribute7 === "LCN";

  const handleSubmit = async (values: typeof form.values) => {
    open();
    const res = await repositoryMdm.post<MessageResponse<boolean>>(
      isEdit
        ? "/api/v1/TblWarrantyCustomerGroup/update"
        : "/api/v1/TblWarrantyCustomerGroup/create",
      {
        ...values,
        id: data?.id ?? 0,
        // Nhóm KBH không dùng thông tin chi nhánh
        attribute1: isBranch ? values.attribute1 : null,
        attribute3: isBranch ? values.attribute3 : null,
        attribute5: isBranch ? values.attribute5 : null,
        attribute6: isBranch ? values.attribute6 : null,
      },
    );
    close();
    if (res?.success) {
      NotificationExtension.Success(
        isEdit ? "Chỉnh sửa thành công !" : "Thêm mới thành công !",
      );
      modals.closeAll();
      onSaved();
    }
  };

  useEffect(() => {
    getProvinceSelectNew().then((items) =>
      setDataProvince(
        items
          ?.filter((item: any) => item.id != null && item.name != null)
          ?.map((item: any) => ({
            value: String(item.id),
            label: String(item.name),
          })) ?? [],
      ),
    );
  }, []);

  useEffect(() => {
    if (!form.values.attribute1) {
      setDataCommune([]);
      return;
    }
    getCommuneSelectNew(String(form.values.attribute1)).then((items) =>
      setDataCommune(
        items
          ?.filter((item: any) => item.value != null && item.text != null)
          ?.map((item: any) => ({
            value: String(item.value),
            label: String(item.text),
          })) ?? [],
      ),
    );
  }, [form.values.attribute1]);

  return (
    <Box
      component="form"
      mx="auto"
      w={{ base: "300px", md: "500px", lg: "600px" }}
      onSubmit={form.onSubmit(handleSubmit)}
      style={{ position: "relative" }}
    >
      <LoadingOverlay
        visible={visible}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />
      <Grid mt={10}>
        <Grid.Col span={4}>
          <TextInput
            label="Mã nhóm"
            placeholder="Ví dụ: BHBT"
            withAsterisk
            disabled={isEdit}
            description={isEdit ? "Mã đã lưu trên phiếu, không đổi được" : ""}
            {...form.getInputProps("code")}
          />
        </Grid.Col>
        <Grid.Col span={8}>
          <TextInput
            label="Tên nhóm"
            placeholder="Ví dụ: BH bảo trì"
            withAsterisk
            {...form.getInputProps("name")}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Select
            label="Loại nhóm"
            data={customerGroupTypes}
            withAsterisk
            allowDeselect={false}
            {...form.getInputProps("attribute7")}
          />
        </Grid.Col>
        {isBranch && (
          <>
            <Grid.Col span={6}>
              <TextInput
                label="Số điện thoại"
                placeholder="Nhập số điện thoại"
                {...form.getInputProps("attribute6")}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Select
                label="Tỉnh/TP"
                placeholder="Chọn tỉnh/thành phố"
                data={dataProvince}
                searchable
                clearable
                nothingFoundMessage="Không tìm thấy dữ liệu !"
                {...form.getInputProps("attribute1")}
                onChange={(value) => {
                  form.setFieldValue("attribute1", value);
                  form.setFieldValue("attribute3", null);
                }}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Select
                label="Phường/Xã"
                placeholder="Chọn phường/xã"
                data={dataCommune}
                searchable
                clearable
                disabled={!form.values.attribute1}
                nothingFoundMessage="Không tìm thấy dữ liệu !"
                {...form.getInputProps("attribute3")}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Địa chỉ cụ thể"
                placeholder="Nhập địa chỉ cụ thể"
                {...form.getInputProps("attribute5")}
              />
            </Grid.Col>
          </>
        )}
      </Grid>

      <Checkbox
        label="Sử dụng"
        mt={10}
        {...form.getInputProps("active", { type: "checkbox" })}
      />

      <Group justify="end" mt="md">
        <Button
          type="button"
          color="gray"
          loading={visible}
          onClick={() => modals.closeAll()}
          leftSection={<IconWindow size={18} />}
        >
          Đóng
        </Button>
        <Button
          type="submit"
          loading={visible}
          leftSection={<IconCheck size={18} />}
        >
          Lưu
        </Button>
      </Group>
    </Box>
  );
};

export default WarrantyCustomerGroupForm;

type WarrantyCustomerGroupFormProps = {
  data?: any;
  onSaved: () => void;
};
